import React, { useEffect, useMemo, useState } from 'react';
import { mockDocuments, InvoiceDocument } from './documentReviewTypes';
import { ImageViewerPanel } from './ImageViewerPanel';
import { InvoiceDocument as ExtendedInvoiceDocument } from '../types/invoice';
const STORAGE_KEY_DOCUMENT_ID = 'current_document_id';
// Same adapter used in DocumentManagementScreen
function adaptDocument(doc: InvoiceDocument): ExtendedInvoiceDocument {
  return {
    ...doc,
    matchingType: doc.matchingType || 'non-po',
    lineItems: doc.lineItems.map((item, index) => ({
      ...item,
      id: item.id || `line-${doc.id}-${index}`,
      taxCode: item.taxCode || 'I',
      taxAmt: typeof item.taxAmt === 'string' ? parseFloat(item.taxAmt) || 0 : item.taxAmt
    }))
  } as ExtendedInvoiceDocument;
}
export function DetachedViewer() {
  const initialDocId = localStorage.getItem(STORAGE_KEY_DOCUMENT_ID) || '1';
  const [currentDocument, setCurrentDocument] = useState<InvoiceDocument>(mockDocuments[initialDocId] || mockDocuments['1']);
  const [isConnected, setIsConnected] = useState(false);
  const adaptedDocument = useMemo(() => adaptDocument(currentDocument), [currentDocument]);
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'DOCUMENT_CHANGED' && event.data.documentId) {
        const doc = mockDocuments[event.data.documentId];
        if (doc) {
          setCurrentDocument(doc);
          setIsConnected(true);
        }
      }
    };
    window.addEventListener('message', handleMessage);
    // Notify the parent window that we're ready
    if (window.opener && !window.opener.closed) {
      window.opener.postMessage({
        type: 'VIEWER_READY'
      }, '*');
      setIsConnected(true);
    }
    // Poll to detect if parent window was closed
    const interval = setInterval(() => {
      if (window.opener && window.opener.closed) {
        setIsConnected(false);
      }
    }, 2000);
    return () => {
      window.removeEventListener('message', handleMessage);
      clearInterval(interval);
    };
  }, []);
  // Also listen for localStorage changes (fallback sync)
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY_DOCUMENT_ID && event.newValue) {
        const doc = mockDocuments[event.newValue];
        if (doc) setCurrentDocument(doc);
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);
  return <div className="h-screen bg-gray-50 flex flex-col" data-id="element-3956">
      {/* Header Bar */}
      <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between flex-shrink-0" data-id="element-3957">
        <div className="flex items-center gap-3" data-id="element-3958">
          <div className={`w-2 h-2 rounded-full ${isConnected ? 'bg-green-500 animate-pulse' : 'bg-amber-500'}`} data-id="element-3959"></div>
          <h1 className="text-lg font-semibold text-gray-900" data-id="element-3960">
            Document Viewer
          </h1>
          <span className="text-sm text-gray-500" data-id="element-3961">—</span>
          <span className="text-sm font-medium text-gray-700" data-id="element-3962">
            {currentDocument.invoiceNumber}
          </span>
          <span className="text-xs text-gray-400" data-id="element-3963">
            ({currentDocument.supplier})
          </span>
        </div>
        <div className="flex items-center gap-3" data-id="element-3964">
          <span className={`text-xs px-2 py-1 rounded-full ${isConnected ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-id="element-3965">
            {isConnected ? 'Synced with main window' : 'Disconnected'}
          </span>
        </div>
      </div>

      {/* Full ImageViewerPanel */}
      <div className="flex-1 overflow-hidden" data-id="element-3966">
        <ImageViewerPanel document={adaptedDocument} data-id="element-3967" />
      </div>
    </div>;
}